import { metadataHook } from '@hooks/metadata';
import { type MouseEvent, type ReactNode, useMemo, useState } from 'react';
import { cn, Tooltip } from '@skupreme/shared/ui';
import { useMetadata } from '@contexts/org-metadata';
import { CommonUtility } from '@utility/common';
import PermissionTooltip from '@components/PermissionTooltip';
import { DarkPopup } from '@elements/Popup';
import { tooltipTexts } from '@utility/constants/common';
import { BoldText } from '@elements/Common';
import { MarketplacePriority } from '@utility/constants/catalog';
import { getTabPermission, useAuth } from '@contexts/auth';
import { LogoAvatar } from '@page-components/onboarding/Connecting/MarketBar';
import { PermissionTabKey } from '@utility/constants/permission';
import { Check, Pause, Play, Warning } from '@phosphor-icons/react';
import { OrganisationAvatar } from './ImageAvatar';
import {
  getMarketplaceIcon,
  getMarketplaceImageSrc,
} from '@utility/marketplace';

const SYNC_ICONS: Record<string, ReactNode> = {
  check: <Check size={11} weight="bold" aria-hidden />,
  pause: <Pause size={11} weight="fill" aria-hidden />,
  play: <Play size={11} weight="fill" aria-hidden />,
};

/* The corner badge on a marketplace tile; its fill says whether sync runs. */
const SYNC_BADGE =
  'sk:absolute sk:-right-2 sk:-bottom-2 sk:z-10 sk:flex sk:size-5 sk:items-center sk:justify-center sk:rounded-full sk:text-surface';

/* Small corner labels (MF, NARF, Seed) that sit over the tile's edge. */
const CORNER_TAG =
  'sk:absolute sk:z-10 sk:rounded-xs sk:px-1 sk:py-px sk:text-[10px] sk:leading-3 sk:font-medium sk:text-surface';

/*
 * `ui image` stays on the logo: screens that host MarketplaceIcon still size
 * and recolour it through `.ui.image` and `a > img` selectors of their own.
 */
const MARKET_IMAGE =
  'ui image sk:size-9 sk:bg-transparent sk:p-1 sk:object-contain';

type HoverIconProps = {
  connected?: boolean | string;
  integrate?: boolean;
  noHover?: boolean;
  icon?: string | null;
  hoverIcon?: string | null;
  toggleSync?: (event: MouseEvent<HTMLSpanElement>) => void;
};

export function HoverIcon({
  connected,
  integrate,
  noHover,
  icon,
  hoverIcon,
  toggleSync,
}: HoverIconProps) {
  const [isEntered, setIsEntered] = useState(false);
  const shownIcon = isEntered ? hoverIcon : icon;

  return (
    <DarkPopup
      position="bottom center"
      padding="0.5rem 1rem"
      setOpen={noHover ? undefined : setIsEntered}
      trigger={
        // biome-ignore lint/a11y/useKeyWithClickEvents: the badge sits inside the tile's link, which cannot nest a button; the tooltip's focusable wrapper is its keyboard stop
        // biome-ignore lint/a11y/noStaticElementInteractions: as above
        <span
          className={cn(
            SYNC_BADGE,
            isEntered || (!integrate && connected)
              ? 'sk:bg-warning'
              : connected
                ? 'sk:bg-success'
                : 'sk:bg-danger',
          )}
          onClick={toggleSync}
        >
          {shownIcon ? SYNC_ICONS[shownIcon] : null}
        </span>
      }
    >
      <BoldText>{integrate ? 'Stop Sync' : 'Start Sync'}</BoldText>
    </DarkPopup>
  );
}

type MarketIconProps = {
  className?: string;
  connected?: boolean;
  fallBackText?: string;
  hideConnected?: boolean;
  id?: string;
  image?: string;
  inactive?: boolean;
  integrate?: boolean;
  isSeed?: boolean;
  is_NARF?: boolean;
  itemSync?: boolean;
  link?: string;
  mf_enabled?: boolean;
  noHover?: boolean;
  onClick?: (id: string | undefined) => void;
  shopName?: string;
  showDisconnectedTooltip?: boolean;
  showMF?: boolean;
  showSeed?: boolean;
  tabIndex?: number;
  toggleSync?: () => void;
  type?: string;
};

function MarketIcon({
  className,
  connected,
  fallBackText,
  hideConnected = false,
  id,
  image,
  inactive,
  integrate,
  isSeed = false,
  is_NARF = false,
  itemSync = true,
  link,
  mf_enabled,
  noHover,
  onClick,
  shopName,
  showDisconnectedTooltip = false,
  showMF = true,
  showSeed = false,
  tabIndex,
  toggleSync,
  type = '',
}: MarketIconProps) {
  const { disabled, hidden } = getTabPermission({
    tab: PermissionTabKey.CatalogMarketplaces,
  });

  const isDisabledTooltip = showDisconnectedTooltip && !connected;

  const content = (
    <div
      className={cn(
        'sk:relative sk:z-5 sk:mx-2.5 sk:mb-2.5 sk:rounded-option sk:p-1',
        inactive
          ? 'sk:bg-danger-bg'
          : !integrate
            ? 'sk:bg-caution-bg'
            : 'sk:bg-fill-tertiary',
        !integrate && 'sk:cursor-default! sk:[&_a]:cursor-default!',
        className,
      )}
    >
      {showMF && mf_enabled ? (
        <span
          className={cn(CORNER_TAG, 'sk:-top-2.5 sk:-left-2.5 sk:bg-primary')}
        >
          MF
        </span>
      ) : null}

      {showSeed && isSeed ? (
        <span
          className={cn(CORNER_TAG, 'sk:right-0 sk:bottom-0 sk:bg-success')}
        >
          Seed
        </span>
      ) : null}

      {is_NARF && (
        <span
          className={cn(CORNER_TAG, 'sk:-top-2.5 sk:-right-2.5 sk:bg-primary')}
        >
          NARF
        </span>
      )}

      <a
        href={
          (link && link.includes('https://') ? link : `https://${link}`) || '#'
        }
        onClick={e => {
          if (onClick || !link || disabled) {
            e.preventDefault();
          }
          onClick?.(id);
        }}
        target="_blank"
        rel="noreferrer"
        tabIndex={tabIndex}
        className="sk:block"
      >
        {!image ? (
          <LogoAvatar name={fallBackText} className="sk:ml-0" />
        ) : (
          <img
            alt={type || fallBackText || ''}
            className={cn(MARKET_IMAGE, 'sk:block')}
            src={getMarketplaceImageSrc(image, inactive || !integrate)}
            onLoad={() => window.dispatchEvent(new Event('resize'))}
          />
        )}
        {!hideConnected && connected && (
          <PermissionTooltip
            disabled={disabled}
            hidden={hidden}
            position="bottom right"
          >
            <HoverIcon
              connected={connected}
              toggleSync={e => {
                e.stopPropagation();
                e.preventDefault();
                if (connected && !disabled) toggleSync?.();
              }}
              integrate={integrate}
              icon={connected ? (integrate ? 'check' : 'pause') : null}
              hoverIcon={integrate ? 'pause' : 'play'}
              noHover={!connected || noHover || disabled}
            />
          </PermissionTooltip>
        )}
      </a>
    </div>
  );

  return isDisabledTooltip ? (
    <DarkPopup position="bottom right" trigger={content}>
      {shopName && (
        <>
          <span>Shop Name: </span>
          <b>{shopName}</b>
          <br />
        </>
      )}
      {itemSync
        ? tooltipTexts.itemDisconnected.replace(/\{marketplace}/g, type)
        : tooltipTexts.marketplaceDisconnected.replace('{marketplace}', type)}
    </DarkPopup>
  ) : shopName ? (
    <DarkPopup trigger={content}>{shopName}</DarkPopup>
  ) : (
    content
  );
}

export function ConnectedMarketplaces({
  marketplaces,
  noHover,
  toggleSync,
  checkMarketplaceStatus = false,
  tabIndex,
}) {
  const { metadata } = metadataHook();
  // @ts-expect-error - TS2339: Property 'marketplaces' does not exist on type 'unknown'.
  const { marketplaces: dataMarketplaces } = useMetadata();

  const sortedData = useMemo(() => {
    let temp = Object.keys(marketplaces || {})
      .map(key => {
        const marketData = marketplaces[key];
        return {
          key,
          ...marketData,
          icon: getMarketplaceIcon(key, marketData, metadata, dataMarketplaces),
          priority: MarketplacePriority[key] || Date.now(),
        };
      })
      .sort((a, b) => a.priority - b.priority);

    // filter unconnected marketplaces
    if (checkMarketplaceStatus) {
      // filter out unconnected shops first
      temp = temp.map(market => {
        if (CommonUtility.isValidArray(market.shops)) {
          market.shops = market.shops.filter(shop => shop.connected);
          market.hadShops = true;
        }
        return market;
      });

      // filter out markets with single shops now
      temp = temp.filter(market => {
        if (market.hadShops) {
          return CommonUtility.isValidArray(market.shops);
        }
        return market.connected;
      });
    }

    return temp;
  }, [marketplaces, metadata, checkMarketplaceStatus, dataMarketplaces]);

  return (
    <div className="sk:flex sk:flex-wrap">
      {sortedData.map(item => {
        if (CommonUtility.isValidArray(item.shops)) {
          return item.shops.map(shop => (
            <MarketIcon
              connected={shop.connected}
              fallBackText={item.type}
              image={item.icon}
              inactive={
                marketplaces && (shop.status || '').toLowerCase() === 'inactive'
              }
              integrate={shop.integrate}
              is_NARF={!!shop.is_NARF}
              key={item.key}
              link={shop.link || item.link || ''}
              mf_enabled={shop.mf_enabled}
              noHover={noHover}
              shopName={shop.shopName}
              tabIndex={tabIndex}
              toggleSync={() => toggleSync && toggleSync(item.key, item)}
            />
          ));
        }

        return (
          <MarketIcon
            connected={item.connected}
            fallBackText={item.type}
            image={item.icon}
            inactive={
              marketplaces && (item.status || '').toLowerCase() === 'inactive'
            }
            integrate={item.integrate}
            is_NARF={!!item.is_NARF}
            key={item.key}
            link={item.link || ''}
            mf_enabled={item.mf_enabled}
            noHover={noHover}
            tabIndex={tabIndex}
            toggleSync={() => toggleSync && toggleSync(item.key, item)}
          />
        );
      })}
    </div>
  );
}

export function MarketplaceIcon({
  marketplace,
  extraClasses,
  error,
  fallbackOrg = true,
  onNoImageFunction,
  fallBackText = null,
  shopName,
}) {
  const { metadata } = metadataHook();
  const { currentOrgInfo: currentOrg } = useAuth();
  // @ts-expect-error - TS2339: Property 'marketplaces' does not exist on type 'unknown'.
  const { marketplaces: orgMarketplaces } = useMetadata();

  const iconSrc = useMemo(() => {
    const marketplaceKey =
      typeof marketplace === 'string' ? marketplace : marketplace?.type || '';
    return getMarketplaceIcon(
      marketplaceKey,
      marketplace,
      metadata,
      orgMarketplaces,
    );
  }, [metadata, marketplace, orgMarketplaces]);

  function IconComponent() {
    if (iconSrc) {
      return (
        <img
          alt={
            typeof marketplace === 'string' ? marketplace : marketplace?.type
          }
          src={iconSrc}
          className={cn(MARKET_IMAGE, extraClasses)}
        />
      );
    }

    if (fallbackOrg) {
      return (
        <div className={cn(MARKET_IMAGE, 'sk:inline-block')}>
          <OrganisationAvatar
            className="ui image"
            image={currentOrg?.logo?.url}
            name={fallBackText || currentOrg?.company_name}
            onNoImageFunction={onNoImageFunction}
          />
        </div>
      );
    }
    return <LogoAvatar className="sk:ml-0" name={marketplace.type} />;
  }

  return (
    <a>
      {error && (
        <Tooltip
          placement="bottom start"
          content={
            <div className="sk:max-h-[350px] sk:max-w-[350px] sk:overflow-y-auto">
              {error}
            </div>
          }
        >
          <span
            role="img"
            aria-label="Marketplace error"
            className={cn(SYNC_BADGE, 'sk:-top-2 sk:bottom-auto sk:bg-danger')}
          >
            <Warning size={11} weight="bold" aria-hidden />
          </span>
        </Tooltip>
      )}
      {shopName ? (
        <DarkPopup
          trigger={
            <div>
              <IconComponent />
            </div>
          }
        >
          {shopName}
        </DarkPopup>
      ) : (
        <IconComponent />
      )}
    </a>
  );
}

export function MarketplaceText({ marketplace }) {
  const { metadata } = metadataHook();

  const marketplaceData = useMemo(
    () =>
      // @ts-expect-error - TS2339: Property 'marketplaces' does not exist on type '{}'.
      marketplace && metadata && metadata.marketplaces
        ? // @ts-expect-error - TS2339: Property 'marketplaces' does not exist on type '{}'.
          metadata.marketplaces[marketplace.toLowerCase()] || {}
        : {},
    [metadata, marketplace],
  );

  return <span>{marketplaceData.text}</span>;
}
